<template>
  <import-failed-modal v-if="importFailed" />
  <loading v-else-if="isLoading" textColor="blue" :progress="importProgress" />
  <layout v-else class="bg-gray-100">
    <content-scroll-container class="h-full">
      <box-title-l :lines="2" class="relative px-4">
        <div
          class="absolute top-0 w-12"
          style="margin: 0;left: -1rem; height: 1.33em;"
        >
          <back-button
            class="absolute left-0 text-nav-light"
            style="left: 50%; top: 50%;transform:translate(-50%, -50%)"
            :back-action="() => $router.push({ name: 'start' })"
          />
        </div>
        {{ $t("views.storage_selection.title.main") }}<br />
        {{ $t("views.storage_selection.title.secondary") }}
      </box-title-l>
      <box-logo />
      <content-scroll-inner class="text-chplus-regular">
        <h3 class="font-bold">
          {{ $t("views.storage_selection.content_title") }}
        </h3>
        <div class="mt-6">
          <div
            class="w-full rounded-lg overflow-hidden border-2 border-landingfade-dark"
            @click="accountType = OFFLINE_ACCOUNT"
          >
            <div
              class="text-left mb-2 bg-gradient-blue text-color-white cursor-pointer"
            >
              <h3 class="p-1 font-bold">
                <input
                  type="radio"
                  class="form-radio mx-2"
                  name="accountType"
                  :value="OFFLINE_ACCOUNT"
                  v-model="accountType"
                />
                {{ $t("views.storage_selection.local_account.title") }}
              </h3>
            </div>
            <p class="px-1 pb-3">
              <span class="font-bold">
                {{ $t("views.storage_selection.local_account.main_desc") }}
              </span>
              {{ $t("views.storage_selection.local_account.secondary_desc") }}
            </p>
          </div>
        </div>
        <div class="mt-3">
          <p class="italic">{{ $t("views.storage_selection.or") }}</p>
        </div>
        <div class="mt-3" @click="accountType = ONLINE_ACCOUNT">
          <div
            class="w-full rounded-lg overflow-hidden border-2 border-landing-dark"
          >
            <div
              class="text-left mb-2 bg-landing-dark text-color-white cursor-pointer"
            >
              <h3 class="p-1 font-bold">
                <input
                  type="radio"
                  class="form-radio mx-2"
                  name="accountType"
                  :value="ONLINE_ACCOUNT"
                  v-model="accountType"
                />
                {{ $t("views.storage_selection.online_account.title") }}
              </h3>
            </div>
            <p class="px-1 pb-3">
              <span class="font-bold">
                {{ $t("views.storage_selection.online_account.main_desc") }}
              </span>
              {{ $t("views.storage_selection.online_account.secondary_desc") }}
            </p>
          </div>
        </div>
        <box-text-long v-if="accountType != ''" class="my-8 text-left">
          <ul class="list-disc pl-4 space-y-3 break-words" style="hyphens:auto">
            <li v-if="selectedOfflineAccount">
              {{ $t("views.storage_selection.local_account.warning_2") }}
            </li>
            <li class="font-bold">
              {{ $t("views.storage_selection.warning") }}
            </li>
          </ul>
        </box-text-long>
        <button
          @click="selectOfflineAccount"
          v-if="selectedOfflineAccount"
          class="p-3 my-4 rounded-lg text-white bg-gradient-blue font-bold"
        >
          {{ $t("views.storage_selection.local_account.create_button") }}
        </button>
        <button
          @click="selectOnlineAccount"
          v-else-if="selectedOnlineAccount"
          class="p-3 p-3 my-1 rounded-lg text-white bg-gradient-indigo font-bold"
        >
          {{ $t("views.storage_selection.online_account.create_button") }}
        </button>
      </content-scroll-inner>
    </content-scroll-container>
  </layout>
</template>

<script>
import Layout from "@/components/layouts/LayoutBase";
import ContentScrollContainer from "@/components/layouts/ContentScrollContainer";
import ContentScrollInner from "@/components/layouts/ContentScrollInner";
import BoxTitleL from "@/components/layouts/BoxTitleL";
import BoxTextLong from "@/components/layouts/BoxTextLong";
import BoxLogo from "@/components/layouts/BoxLogo";
import Loading from "@/components/layouts/Loading";
import ImportFailedModal from "@/components/modals/ImportFailedModal";
import { mapGetters, mapMutations } from "vuex";
import constants from "@/constants";
import client from "api-client";
import BackButton from "@/components/buttons/BackButton";
import { RandomHelper } from "@/utils/RandomHelper";
import { LanguageCode } from "@/utils/LanguageCode";

export default {
  name: "StorageSelection",
  components: {
    BackButton,
    ContentScrollContainer,
    ContentScrollInner,
    Layout,
    BoxTitleL,
    BoxLogo,
    BoxTextLong,
    Loading,
    ImportFailedModal
  },
  data() {
    return {
      accountType: "",
      isLoading: false
    };
  },
  beforeRouteEnter(to, from, next) {
    if (process.env.VUE_APP_SUPPORTS_MOBILE_OFFLINE != "true") {
      next({ name: "start" });
      return;
    }

    next();
  },
  methods: {
    ...mapMutations("storage", ["selectOffline", "selectOnline"]),
    async selectOfflineAccount() {
      this.isLoading = true;
      this.selectOffline();

      let language = LanguageCode.getLanguage(this.$i18n.locale);
      const userId = await client.register(
        RandomHelper.getString(16) + "@email.com",
        "offline",
        "offline",
        language
      );
      this.$store.dispatch(
        "store",
        { key: "userId", value: userId },
        { root: true }
      );
      await this.$store.dispatch("user/fetchUser", { userId });

      this.fetchProgress(); // starts loop checking progress
      await this.$store.dispatch("storage/updateCommonData");

      if (!this.importFailed) {
        await this.$router.push({ name: "welcome" });
        this.isLoading = false;
      }
    },
    fetchProgress() {
      if (this.isLoading && !this.importFailed) {
        this.$store.dispatch("storage/fetchImportProgress");
        setTimeout(this.fetchProgress, 200);
      }
    },
    selectOnlineAccount() {
      this.selectOnline();

      this.$router.push({ name: "register" });
    }
  },
  computed: {
    ...mapGetters("user", ["user"]),
    ...mapGetters("storage", [
      "isOnlineUser",
      "importProgress",
      "importFailed"
    ]),
    OFFLINE_ACCOUNT() {
      return constants.OFFLINE_USER;
    },
    ONLINE_ACCOUNT() {
      return constants.ONLINE_USER;
    },
    selectedOfflineAccount() {
      return this.accountType == this.OFFLINE_ACCOUNT;
    },
    selectedOnlineAccount() {
      return this.accountType == this.ONLINE_ACCOUNT;
    }
  },
  beforeDestroy() {
    if (this.importFailed) {
      this.$router.back();
    }
  }
};
</script>
